import type { StyledComponent } from '@emotion/styled';
import styled from '@emotion/styled';
import { Prismic, PrismicContext } from '@landsbankinn/prismic';
import {
  device,
  typography,
  useMedia,
  mobileGutterMargin,
} from '@landsbankinn/ui';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import React, { forwardRef, useContext, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import type { ImgAr, ImgSource } from '../../components/img/Img';
import { Img } from '../../components/img/Img';

export const Host: StyledComponent<any> = styled(animated.div)`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  position: relative;
  width: 100%;
  background-color: ${(props) => props.theme.colors.background};
  cursor: pointer;

  @media ${device.tablet} {
    margin-bottom: 30px;
  }
`;

const ImageLink = styled(Host)`
  margin-bottom: 0;

  @media ${device.tablet} {
    margin-bottom: 0;
  }

  &:after {
    content: '';
    height: 20px;
    width: 100%;

    position: absolute;
    bottom: -20px;
    left: 0;
    right: 0;
  }
`;

const Overflow = styled.div`
  overflow: hidden;
  margin-left: ${(props) => `-${props.theme.containerGutter}px`};
  margin-right: ${(props) => `-${props.theme.containerGutter}px`};
  margin-bottom: 2em;

  ${mobileGutterMargin}

  @media ${device.tablet} {
    margin: 0;
  }
`;

const ImgWrap = styled(animated.div)`
  height: 100%;
  transform: scale(1);

  > *:first-child {
    height: 100%;
  }
`;

const Body = styled.div``;

const Title = styled.h2`
  ${typography.h3};
  margin-bottom: 30px;

  @media ${device.tablet} {
    margin-top: 40px;
  }
`;

const Copy = styled.div`
  ${typography.body}
  margin-bottom: 40px;
`;

const CardAction = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.primary};
  margin-top: auto;
`;

export interface CardProps {
  link?: Prismic.Link;
  title: string | null;
  description: React.ReactNode;
  image?: ImgSource;
  ar?: ImgAr;
  imageAspect?: number;
  action?: React.ReactNode;
  isSplittedCard?: boolean;
  isSingleFeatured?: boolean;
  style?: any;
}

export const OtherCard: FC<CardProps> = forwardRef(function OCard(
  {
    link,
    title,
    description,
    image,
    ar,
    action,
    isSplittedCard,
    style,
  }: CardProps,
  ref,
) {
  const router = useRouter();
  const { urlResolver } = useContext(PrismicContext);
  const url = Prismic.link(urlResolver, link);
  const [isHover, setHover] = useState(false);
  const isMobile = useMedia('sm');

  const onHover = useSpring({
    transform: isHover ? 'scale(1.025)' : 'scale(1)',
  });

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (window?.getSelection?.()?.type === 'Range') {
      e.preventDefault();
      return false;
    }

    return router.push(url.href, url.as);
  };

  const aspectRatio = isMobile ? 'ar_1_1' : ar;

  if (isSplittedCard) {
    return (
      <>
        {!!image && (
          <ImageLink
            ref={ref as any}
            tabIndex={-1}
            onMouseOver={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <Overflow>
              <ImgWrap style={{ ...onHover }}>
                <Img src={image} ar={aspectRatio} />
              </ImgWrap>
            </Overflow>
          </ImageLink>
        )}
        <Host
          style={style}
          ref={ref as any}
          onMouseOver={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <Title>{title}</Title>
          <Body>
            <Copy>{description}</Copy>
          </Body>
          {!!action && <CardAction>{action}</CardAction>}
        </Host>
      </>
    );
  }

  return (
    <Host
      ref={ref as any}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      style={style}
    >
      {!!image && (
        <Overflow>
          <ImgWrap style={{ ...onHover }}>
            <Img src={image} ar={aspectRatio} />
          </ImgWrap>
        </Overflow>
      )}
      <Title>{title}</Title>
      <Body>
        <Copy>{description}</Copy>
      </Body>
      {!!action && <CardAction>{action}</CardAction>}
    </Host>
  );
});
